import { PropsWithChildren, useEffect, useState } from "react"
import { makeStyles } from "tss-react/mui"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Typography,
    Stack,
    useTheme,
    useMediaQuery,
} from "@mui/material"
import { tailwindPalette } from "../../../../../modules/theme/tailwindPalette"

const useStyles = makeStyles()((theme) => ({
    accordion: {
        padding: "0",
        borderRadius: "1rem",
        boxShadow: "none",
        flex: "none",

        "&:first-of-type, &:last-of-type": {
            borderRadius: "1rem",
        },
        "&.Mui-expanded": {
            margin: 0,
        },
        "&:before": {
            display: "none",
        },
        "@media print": {
            "& .MuiCollapse-hidden": {
                height: "auto",
                overflow: "visible",
                visibility: "visible",
            },
        },
    },
    accordionSummary: {
        padding: "0.75rem 1.25rem",
        minHeight: "auto",
        alignItems: "flex-start",
        "&.Mui-expanded": {
            minHeight: "auto",
        },
        "& .MuiAccordionSummary-content": {
            margin: 0,
            "&.Mui-expanded": {
                margin: 0,
            },
        },
        "& .MuiAccordionSummary-expandIconWrapper": {
            "@media print": {
                display: "none !important",
            },
            [theme.breakpoints.down("sm")]: {
                display: "none !important",
            },
        },
        [theme.breakpoints.down("sm")]: {
            backgroundColor: "white !important",
        },
    },
    accordionSummaryBg: {
        backgroundColor: tailwindPalette.slate[100],
    },
    title: {
        color: tailwindPalette.slate[500],
        textTransform: "uppercase",
        fontWeight: 600,
        fontSize: "17px",
        lineHeight: 1.5,
        [theme.breakpoints.down("sm")]: {
            color: theme.palette.text.primary,
            textTransform: "none",
            fontSize: "1.25rem",
            fontWeight: 500,
        },
    },
    accordionDetails: {
        textAlign: "left",
        padding: "1.25rem",
        paddingTop: "0",
        "@media print": {
            padding: 0,
        },
    },
}))

type AccordionSectionProps = Readonly<
    PropsWithChildren<{
        title: string
        subTitle?: JSX.Element
        defaultExpanded?: boolean
        updateAccordionUi?: string
        updateAccordionDetails?: string
        handleExpanded?: () => void
        isTitleBackground?: boolean
    }>
>

export function AccordionSection({
    title,
    subTitle,
    defaultExpanded = false,
    updateAccordionUi = "",
    updateAccordionDetails = "",
    children,
    handleExpanded,
    isTitleBackground = false,
}: AccordionSectionProps) {
    const { classes, cx } = useStyles()
    const [expanded, setExpanded] = useState(defaultExpanded)
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

    const handleChange = () => {
        if (handleExpanded) {
            handleExpanded()
        } else setExpanded((prev) => !prev)
    }

    return (
        <Accordion
            expanded={expanded || isMobile}
            onChange={handleChange}
            className={cx(classes.accordion, updateAccordionUi)}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                className={cx(classes.accordionSummary, {
                    [classes.accordionSummaryBg]: isTitleBackground,
                })}
            >
                <Stack gap=".75rem">
                    <Typography
                        variant={isTitleBackground ? "subtitle1" : "h6"}
                        className={cx({ [classes.title]: isTitleBackground })}
                    >
                        {title}
                    </Typography>
                    {subTitle}
                </Stack>
            </AccordionSummary>
            <AccordionDetails className={cx(classes.accordionDetails, updateAccordionDetails)}>
                {children}
            </AccordionDetails>
        </Accordion>
    )
}
